/**
 * AdjudicatorPanel — phase 6. What the adjudicator proposed, and what the operator does about it.
 *
 * ⛔ APPLYING GOES THROUGH THE ENDPOINTS THAT ALREADY EXIST, and that is the whole design:
 *
 *   kind            → PUT  /voice-rendering/:bookId/line-kind        (undo: kindWas)
 *   character       → POST /voice-rendering/:bookId/reassign-speaker (undo: speakerWas + undo-reassign)
 *   narrator        → POST /voice-rendering/:bookId/reassign-speaker (same)
 *   merge           → the Manage Cast merge, which confirms and can be unmerged
 *
 * Duplicating a write path so a model's suggestion could drive it would be the worst possible place to
 * add a second one. So nothing here is a new kind of change to the book — every button is a change the
 * operator could already make by hand, with the evidence for it sitting beside the button.
 *
 * ⚠️ A KIND CHANGES NO AUDIO UNTIL THAT KIND HAS A VOICE. Marking a line `announcement` and stopping
 * there leaves it exactly as it was — the panel says so, because "Signage · no voice" reading as
 * handled is precisely the failure the kind rung was built to avoid.
 *
 * ⛔ A MERGE IS NOT OFFERED AS A BUTTON HERE. It is a LitGroup-wide act with its own confirmation and
 * its own undo in Manage Cast, and a one-click merge driven by a model's guess is how "voice" (ch49)
 * was once folded into a ch33 machine unseen. The proposal is shown; the act stays where it belongs.
 *
 * See docs/journal/PHASE_5_6_ADJUDICATOR_AND_SWEEP_SPEC.md.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Segment, Header, Icon, Button, Message, Label, Table, Popup } from 'semantic-ui-react';
import { apiClient } from '../../apiTools/apiClient';
import ConfirmModal from '../prototype/ConfirmModal';

const CONF_COLOR = { high: 'green', medium: 'yellow', low: 'grey' };

/**
 * ⭐ WHICH RULE PUT THIS ROW HERE. The operator asked it in exactly those words — "Glendyl has a
 * designed voice, and the text supports a normal dialog flow. What rule puts them there?" — and the
 * panel had no answer, so I had to go and read the candidate filter for them. A worklist that cannot
 * say why an item is on it teaches the operator to distrust the whole list rather than one rule.
 *
 * The four classes are the candidate filter's, not the model's: see server/services/dialogProcessing/
 * speakerAdjudicator.js → buildCandidates.
 */
const WHY_COPY = {
  'no-speaker': { short: 'no speaker', color: 'red', tip: 'The line has no speaker label at all, so nothing can resolve it to a voice.' },
  'descriptive-label': { short: 'descriptive label', color: 'orange', tip: 'The label describes rather than names — "neutral voice", "The docpod" — so it is probably a kind, not a character.' },
  'unknown-label': { short: 'unknown label', color: 'yellow', tip: 'A label that names somebody, but nobody on the roster — often a variant of a character who is already cast.' },
  'resolved-but-suspicious': { short: 'reads as lettering', color: 'blue', tip: 'This line ALREADY resolves to a voice. It is here only because the narration beside it announces printed text ("were the words", "the sign read"), which is how a stencil ends up read aloud by a character. Nothing was wrong with it until you say so.' },
};

/**
 * ⚠️ A PROPOSAL THAT CHANGES NOTHING. The model sometimes confirms the attribution the book already
 * has — "Uncle Raven → Uncle Raven" — which is a useful answer and a useless button: applying it
 * reassigns a line to the character it is already assigned to. 11 of Luck's 49 proposals were these.
 * Named, so the operator can dismiss them without weighing them as changes.
 */
const isNoOp = (p) => (p.proposal === 'character'
  && !!p.characterName
  && [p.currentlyAttributedTo, p.speakerLabel]
    .some((s) => s && String(s).trim().toLowerCase() === String(p.characterName).trim().toLowerCase()));

const describe = (p) => {
  if (p.proposal === 'kind') return `kind: ${p.kind}`;
  if (p.proposal === 'merge') return `merge into ${p.characterName}`;
  if (p.proposal === 'character') return `${p.characterName} speaks it`;
  if (p.proposal === 'narrator') return 'the Narrator reads it';
  return 'leave it alone';
};

/**
 * ⛔ A KIND PROPOSAL THAT WILL NOT BE HEARD.
 *
 * A kind lends its voice only to a character that has NONE. So proposing `signage` for a line whose
 * speaker is cast is a correct judgement about the line's FORM and changes nothing about its sound.
 *
 * ⭐ This cost the operator an evening. "Humbecker Ford 1.8 miles." → `kind: signage` was right (it is a
 * waysign, not Father-Mayor Gullwimple speaking), Apply reported success, the run recorded `applied`,
 * and the line still reads in Gullwimple's voice — measured again with `signage` voiced: still his.
 *
 * ⚠️ `=== true` only. `lineVoiceInfo` covers the selected unit, so an absent entry means "we cannot
 * tell", and a warning drawn on a guess is worse than none.
 */
const kindWillBeOutranked = (p, lineVoiceInfo) => (
  p.proposal === 'kind' && (lineVoiceInfo || {})[p.lineId]?.speakerHasOwnVoice === true
);

const AdjudicatorPanel = ({ litGroupUuid, bookId, bookTitle, onApplied, lineVoiceInfo, onBorrowKindVoice }) => {
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [confirmRun, setConfirmRun] = useState(false);
  const [showDone, setShowDone] = useState(false);
  // ⭐ THE PASSAGE, ON DEMAND. The operator's report: judging a proposal meant opening the chapter to
  // find the line, which is exactly the hunt the evidence was supposed to save. Two lines either side
  // ride along with every proposal; "more" fetches a wider window for the one row being weighed.
  const [wide, setWide] = useState({});        // lineId → { loading, lines[] } | null

  const load = useCallback(async () => {
    if (!litGroupUuid || !bookId) return;
    try {
      const r = await apiClient.get(`/litgroups/${litGroupUuid}/books/${bookId}/adjudicator`);
      setRun(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not read the proposals');
    }
  }, [litGroupUuid, bookId]);

  useEffect(() => { load(); }, [load]);

  /**
   * ⚠️ The only call here that spends money — behind a confirmation, never on page load.
   *
   * ⛔ AND THE ONLY ONE THAT OUTLASTS THE DEFAULT TIMEOUT. The API client times out at 30s, which is
   * fine for reads and small writes and far too short for this: judging a book's residue took ~45s on
   * Luck's 27 lines, so the first real Ask returned ECONNABORTED while the server finished the work and
   * stored it. The request needs its own timeout — and, because the work is stored, a timeout is
   * RECOVERABLE rather than lost: if it happens anyway (a slow book, a proxy, a bigger batch), the
   * panel polls the stored run instead of telling the operator it failed and charging them again.
   */
  const RUN_TIMEOUT_MS = 10 * 60 * 1000;

  const pollForRun = async (since) => {
    // The run is on the Book node the moment the server finishes, so this is a read, not a retry.
    for (let i = 0; i < 20; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 5000));
      try {
        // eslint-disable-next-line no-await-in-loop
        const r = await apiClient.get(`/litgroups/${litGroupUuid}/books/${bookId}/adjudicator`);
        if (r.data && r.data.runAt && r.data.runAt !== since) return r.data;
      } catch (_) { /* keep waiting — the server is busy, not broken */ }
    }
    return null;
  };

  const doRun = async () => {
    setConfirmRun(false); setRunning(true); setError(null); setMsg(null);
    const since = run?.runAt || null;
    try {
      const r = await apiClient.post(
        `/litgroups/${litGroupUuid}/books/${bookId}/adjudicator/run`, {}, {},
        { timeout: RUN_TIMEOUT_MS },
      );
      setRun(r.data);
      const s = r.data.stats || {};
      setMsg(
        `Asked about ${s.candidates ?? 0} line(s): ${(r.data.proposals || []).length} proposal(s)`
        + `${s.unanswered ? `, ${s.unanswered} unanswered` : ''}`
        + `${s.refusals ? `, ${s.refusals} refused` : ''}`
        + `${s.usage ? ` · ${s.usage.input} in / ${s.usage.output} out tokens (${s.usage.cacheRead} cached)` : ''}.`,
      );
    } catch (e) {
      // ⚠️ A timeout is NOT a failure here — the server stores the run as its last act, so the answer
      // is very likely already there. Look before reporting, and never invite a second charge for work
      // that has been done.
      if (e.code === 'ECONNABORTED' || /timeout/i.test(e.message || '')) {
        setMsg('Still working — this took longer than the request allows. Waiting for the stored result…');
        const fresh = await pollForRun(since);
        if (fresh) {
          setRun(fresh);
          setMsg(`Finished: ${(fresh.proposals || []).filter((x) => x.status === 'open').length} open proposal(s). `
            + 'The request had already timed out, so this was read back rather than asked again.');
          return;
        }
        setError('The request timed out and no stored run appeared. Check the server log before asking again — the work may have completed.');
        return;
      }
      setError(e.response?.data?.error || 'Could not run the adjudicator');
    } finally { setRunning(false); }
  };

  const loadWide = async (p) => {
    if (wide[p.lineId]) { setWide((w) => ({ ...w, [p.lineId]: null })); return; }   // toggle closed
    setWide((w) => ({ ...w, [p.lineId]: { loading: true, lines: [] } }));
    try {
      const r = await apiClient.get(`/voice-rendering/${bookId}/lines/${p.lineId}/context`, {}, { window: 8 });
      const data = r.data?.data || r.data || {};
      setWide((w) => ({ ...w, [p.lineId]: { loading: false, lines: data.lines || [] } }));
    } catch (e) {
      setWide((w) => ({ ...w, [p.lineId]: { loading: false, lines: [], error: e.response?.data?.error?.message || 'Could not read the passage' } }));
    }
  };

  const mark = async (p, status, actualAction) => {
    await apiClient.post(`/litgroups/${litGroupUuid}/books/${bookId}/adjudicator/mark`,
      { lineId: p.lineId, proposal: p.proposal, status, actualAction });
  };

  /** Apply one proposal through the endpoint that already owns that change. */
  const apply = async (p) => {
    // ⛔ Belt and braces. The button is no longer drawn for a confirmation, but this is the function
    // that spends the write — an S3 rewrite of the whole script — so it refuses one itself rather than
    // trusting that every future caller remembers.
    if (isNoOp(p)) {
      setMsg(`${p.characterName} already speaks that line — nothing to apply. Mark it confirmed instead.`);
      return;
    }
    setBusy(p.lineId); setError(null); setMsg(null);
    try {
      let actual = null;
      if (p.proposal === 'kind') {
        await apiClient.put(`/voice-rendering/${bookId}/line-kind`, { lineIds: [p.lineId], kind: p.kind });
        actual = `kind:${p.kind}`;
      } else if (p.proposal === 'character' || p.proposal === 'narrator') {
        const speakerName = p.proposal === 'narrator' ? 'Narrator' : p.characterName;
        await apiClient.post(`/voice-rendering/${bookId}/reassign-speaker`, { lineIds: [p.lineId], speakerName });
        actual = `${p.proposal}:${speakerName}`;
      } else {
        // merge / leave are not applied from here — see the header.
        setBusy(null);
        return;
      }
      await mark(p, 'applied', actual);
      /**
       * ⛔ AND IT SAYS SO WHEN THE APPLY CHANGED NOTHING AUDIBLE. "Applied: kind: signage. Reversible."
       * was true and deeply misleading: the kind was stored, and the line went on reading in its cast
       * speaker's voice. The message now names the speaker, the reason, and the tool that would work.
       */
      if (kindWillBeOutranked(p, lineVoiceInfo)) {
        const who = lineVoiceInfo[p.lineId]?.speaker || 'that speaker';
        setMsg(`Recorded: ${describe(p)} — but it will NOT change the audio, because ${who} has a voice `
          + 'of their own and a kind only lends its voice to a character that has none. '
          + 'Use "borrow the kind\'s voice" to make it audible, or reassign the line to an uncast label.');
      } else {
        setMsg(`Applied: ${describe(p)}. Reversible — ${p.proposal === 'kind' ? 'the previous kind is kept' : 'the previous speaker is kept'}.`);
      }
      await load();
      if (onApplied) onApplied(p);
    } catch (e) {
      setError(e.response?.data?.error?.message || e.response?.data?.error || 'Could not apply that proposal');
    } finally { setBusy(null); }
  };

  const dismiss = async (p) => {
    setBusy(p.lineId); setError(null);
    try {
      await mark(p, 'dismissed', null);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not record that');
    } finally { setBusy(null); }
  };

  const { open, done } = useMemo(() => {
    const all = (run && run.proposals) || [];
    return { open: all.filter((p) => p.status === 'open'), done: all.filter((p) => p.status !== 'open') };
  }, [run]);

  const groups = useMemo(() => {
    const g = { kind: [], merge: [], character: [], narrator: [], leave: [] };
    open.forEach((p) => { (g[p.proposal] || (g[p.proposal] = [])).push(p); });
    return g;
  }, [open]);

  if (!litGroupUuid || !bookId) return null;

  const GROUP_COPY = {
    kind: { title: 'Nobody speaks these — they are a kind of text', note: '⚠️ Marking a kind changes no audio until that kind has a voice, in the panel above.' },
    character: { title: 'A character on the roster speaks these', note: 'Reassigns the line. The previous label is kept, so it can be undone.' },
    narrator: { title: 'These are narration', note: 'Reassigns to the Narrator. Reversible.' },
    merge: { title: 'These labels are another name for someone', note: '⛔ Do these in Manage Cast — a merge is LitGroup-wide, has its own confirmation and its own undo.' },
    leave: { title: 'It could not tell', note: 'Left for you. Dismiss to stop it being offered again.' },
  };

  return (
    <Segment className="rs-adjudicator">
      <Header as="h5" style={{ marginBottom: '0.25em' }}>
        <Icon name="magic" /> Unsettled speakers — proposals
      </Header>
      <div style={{ fontSize: '0.82em', color: '#777', marginBottom: '0.75em' }}>
        Lines whose speaker this book cannot settle, with a suggestion and the evidence for it.
        Applying uses the same reassign / kind / merge you would use by hand, so every one is reversible.
        {run?.runAt && <> Last asked {new Date(run.runAt).toLocaleString()}{run.model ? ` · ${run.model}` : ''}.</>}
      </div>

      {error && <Message negative size="tiny" onDismiss={() => setError(null)}>{error}</Message>}
      {msg && <Message positive size="tiny" onDismiss={() => setMsg(null)}>{msg}</Message>}
      {run && run.credentials === false && (
        <Message warning size="tiny">
          <Icon name="key" /> The server has no ANTHROPIC_API_KEY, so nothing can be asked yet.
        </Message>
      )}

      <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center', marginBottom: '0.6em' }}>
        <Button size="small" primary basic loading={running} disabled={running || run?.credentials === false}
          onClick={() => setConfirmRun(true)}>
          <Icon name="search" /> {run?.runAt ? 'Ask again' : 'Ask'}
        </Button>
        <span style={{ color: '#999', fontSize: '0.85em' }}>
          {open.length} open · {done.length} decided
        </span>
      </div>

      {Object.entries(groups).filter(([, list]) => list.length).map(([proposal, list]) => (
        <div key={proposal} style={{ marginBottom: '0.8em' }}>
          <div style={{ fontWeight: 600, fontSize: '0.9em' }}>{GROUP_COPY[proposal]?.title || proposal} ({list.length})</div>
          <div style={{ fontSize: '0.8em', color: '#888', marginBottom: '0.3em' }}>{GROUP_COPY[proposal]?.note}</div>
          <Table compact="very" celled unstackable>
            {/* Seven unlabelled columns read as a wall — and the one that matters least by width
                (confidence) looks like the one that matters most without a name on it. */}
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell collapsing>Ch</Table.HeaderCell>
                <Table.HeaderCell collapsing>Label in the book</Table.HeaderCell>
                <Table.HeaderCell collapsing>Why it was asked</Table.HeaderCell>
                <Table.HeaderCell collapsing>Proposal</Table.HeaderCell>
                <Table.HeaderCell collapsing>Sure?</Table.HeaderCell>
                <Table.HeaderCell>Evidence — read this, not the proposal</Table.HeaderCell>
                <Table.HeaderCell collapsing />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {list.map((p) => (
                <Table.Row key={`${p.lineId}-${p.proposal}`}>
                  <Table.Cell collapsing>{p.chapterNumber ?? '—'}</Table.Cell>
                  <Table.Cell collapsing style={{ whiteSpace: 'nowrap' }}>{p.speakerLabel || <em>(no speaker)</em>}</Table.Cell>
                  {/* ⭐ The rule, named. "Reads as lettering" is the only class that arrives about a
                      line nothing was wrong with, and the operator has to be able to see that at a
                      glance instead of asking. */}
                  <Table.Cell collapsing style={{ whiteSpace: 'nowrap' }}>
                    {WHY_COPY[p.why] ? (
                      <Popup size="tiny" wide="very" content={WHY_COPY[p.why].tip}
                        trigger={<Label size="mini" basic color={WHY_COPY[p.why].color}>{WHY_COPY[p.why].short}</Label>} />
                    ) : <span style={{ color: '#bbb' }}>—</span>}
                  </Table.Cell>
                  <Table.Cell collapsing>
                    <strong>{describe(p)}</strong>
                    {kindWillBeOutranked(p, lineVoiceInfo) && (
                      <Popup size="small" wide="very"
                        content={`${lineVoiceInfo[p.lineId]?.speaker || 'This speaker'} has a voice of their own, `
                          + 'and a kind only lends its voice to a character that has none. Applying this records '
                          + 'the right judgement about the line and changes nothing you can hear — use "borrow '
                          + 'the kind\'s voice" for that, or reassign the line to an uncast label.'}
                        trigger={<Label size="mini" basic color="red" style={{ marginLeft: 4 }}>won’t be heard</Label>} />
                    )}
                    {isNoOp(p) && (
                      <Popup size="tiny" wide
                        content="This is what the line already says. It is a confirmation, not a correction, so there is nothing to apply — mark it confirmed. (Applying one used to stamp a reassignment that never happened and rewrite the script in S3; that is why the button is gone.)"
                        trigger={<Label size="mini" basic style={{ marginLeft: 4 }}>already correct</Label>} />
                    )}
                  </Table.Cell>
                  <Table.Cell collapsing>
                    <Popup size="tiny" content="How sure it said it was. Read the evidence either way."
                      trigger={<Label size="mini" color={CONF_COLOR[p.confidence] || 'grey'}>{p.confidence}</Label>} />
                  </Table.Cell>
                  {/* ⭐ The evidence is the point. A proposal without it would be a guess with a label,
                      and the operator is reading this column, not the verdict. Below it, the passage —
                      because a verdict about a line you cannot see is not reviewable. */}
                  <Table.Cell style={{ fontSize: '0.85em', color: '#555' }}>
                    <div>{p.evidence}</div>
                    {(p.text || p.before?.length || p.after?.length) && (
                      <div style={{
                        marginTop: 4, paddingLeft: 8, borderLeft: '3px solid #e8e8e8',
                        fontFamily: 'Georgia, serif', color: '#444', lineHeight: 1.4,
                      }}>
                        {(p.before || []).map((l, i) => <div key={`b${i}`} style={{ color: '#999' }}>{l}</div>)}
                        {p.text && (
                          <div style={{ fontWeight: 600 }}>
                            {p.currentlyAttributedTo ? `${p.currentlyAttributedTo}: ` : (p.speakerLabel ? `${p.speakerLabel}: ` : '')}
                            {p.text}
                          </div>
                        )}
                        {(p.after || []).map((l, i) => <div key={`a${i}`} style={{ color: '#999' }}>{l}</div>)}
                        <Button size="mini" basic compact style={{ marginTop: 3 }}
                          loading={wide[p.lineId]?.loading} onClick={() => loadWide(p)}>
                          <Icon name={wide[p.lineId] ? 'caret down' : 'ellipsis horizontal'} />
                          {wide[p.lineId] ? 'less' : 'more'}
                        </Button>
                        {wide[p.lineId] && !wide[p.lineId].loading && (
                          <div style={{ marginTop: 4, background: '#fafafa', padding: '4px 6px', borderRadius: 3 }}>
                            {wide[p.lineId].error && <span style={{ color: '#b32424' }}>{wide[p.lineId].error}</span>}
                            {(wide[p.lineId].lines || []).map((l) => (
                              <div key={l.lineId || l.lineIndex}
                                style={{
                                  color: l.isTarget ? '#111' : '#777',
                                  fontWeight: l.isTarget ? 600 : 400,
                                  background: l.isTarget ? '#fff8dc' : undefined,
                                  padding: l.isTarget ? '1px 3px' : undefined,
                                }}>
                                <span style={{ color: '#aaa' }}>{l.speaker || '—'}{l.utteranceKind ? ` [${l.utteranceKind}]` : ''}: </span>
                                {l.content}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </Table.Cell>
                  <Table.Cell collapsing>
                    {/*
                      ⛔ NO APPLY BUTTON ON A CONFIRMATION, and it took real damage to earn that. It
                      was demoted from `primary` when the class was first named, which was not enough:
                      the operator applied seven of them, and each click stamped `speakerWas` on a line
                      that never moved, invented a "reassigned to itself" hint in the Casting Studio,
                      and rewrote the book's whole scriptJson in S3 to store the value already there.
                      A button that can only do harm should not be drawn — the useful act here is
                      "confirmed", which is what dismiss records.
                    */}
                    {(p.proposal === 'kind' || p.proposal === 'character' || p.proposal === 'narrator')
                      && !isNoOp(p) && (
                      <Button size="mini" primary basic disabled={busy === p.lineId} loading={busy === p.lineId}
                        onClick={() => apply(p)}>apply</Button>
                    )}
                    {/*
                      ⭐ THE SECOND HALF OF A KIND DECISION THE KIND CANNOT CARRY OUT ALONE, and a
                      SEPARATE button on purpose. Borrowing the kind's voice sets `spokenInVoiceOf`,
                      which really does change the audio — so it is never folded into Apply. Seven
                      confirmation proposals were applied as reassignments this session because a button
                      did more than its label said.
                    */}
                    {kindWillBeOutranked(p, lineVoiceInfo) && onBorrowKindVoice && (
                      <Popup size="tiny" wide
                        content={`Keep ${lineVoiceInfo[p.lineId]?.speaker || 'the speaker'} as who says this line, `
                          + `but have the ${p.kind} voice read it. The attribution and the line count do not move.`}
                        trigger={
                          <Button size="mini" primary basic disabled={busy === p.lineId}
                            onClick={() => onBorrowKindVoice(p.lineId, p.kind)}>
                            borrow the {p.kind} voice
                          </Button>
                        } />
                    )}
                    <Button size="mini" basic disabled={busy === p.lineId} onClick={() => dismiss(p)}>
                      {isNoOp(p) ? 'confirmed' : 'dismiss'}
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      ))}

      {!open.length && run?.runAt && (
        <Message size="tiny" positive><Icon name="check" /> Nothing open — every proposal has been decided.</Message>
      )}

      {/* ⚠️ The decided list stays visible, because what a model suggested about an author's book and
          what a human did about it is a record worth keeping — the same reasoning as the editorial
          overlay's history. */}
      {done.length > 0 && (
        <div>
          <Button size="mini" basic compact onClick={() => setShowDone((v) => !v)}>
            <Icon name={showDone ? 'caret down' : 'caret right'} />{done.length} decided
          </Button>
          {showDone && (
            <div style={{ fontSize: '0.82em', marginTop: '0.3em' }}>
              {done.map((p) => (
                <div key={`${p.lineId}-${p.proposal}`} style={{ color: '#888', padding: '0.15em 0' }}>
                  <Label size="mini" basic color={p.status === 'applied' ? 'green' : undefined}>{p.status}</Label>{' '}
                  ch{p.chapterNumber ?? '—'} · {p.speakerLabel || '(no speaker)'} → {describe(p)}
                  {p.actualAction && p.actualAction !== describe(p) && <> · did: {p.actualAction}</>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        open={confirmRun}
        title="Ask about the unsettled lines?"
        icon="magic"
        danger={false}
        items={[bookTitle || 'this book']}
        note="This sends the unsettled lines and the cast list to Claude and costs a small amount — usually cents for a book's residue. It proposes only: nothing is changed until you apply a proposal, and decisions you have already made are not asked about again."
        confirmLabel="Ask"
        loading={running}
        onConfirm={doRun}
        onCancel={() => setConfirmRun(false)}
      />
    </Segment>
  );
};

export default AdjudicatorPanel;
